/**
 * Money on screen: the jurisdiction's currency, its digit grouping, two decimals.
 *
 * The API sends a Postgres NUMERIC as a string because it does not survive a double, and
 * this module is the one place it becomes a number -- at the last moment, on its way into
 * a DOM node. Nothing formatted here is ever sent back.
 *
 * Grouping is not cosmetic and not the browser's to decide. `en-IN` reads 12,50,000, which
 * is what the person checking an invoice against a ledger in Mumbai counts in; `en-US`
 * reads 1,250,000, which is what the same person counts in in Houston. Either one shown to
 * the other has to be read digit by digit before it can be compared, so the locale follows
 * the deployment's tax regime (ADR-013) rather than the machine the browser is on.
 *
 * Two ways in, for two different questions:
 *
 * * `setMoneyDefaults` -- what this deployment bills in. Set once from the regime, used by
 *   every screen that shows a figure of its own: dashboards, ledgers, order totals.
 * * the `currency` and `locale` arguments -- what *this document* was billed in. An
 *   invoice carries its own, frozen at the moment it was raised, and a client who switched
 *   jurisdiction last month must still see last month's invoices in dollars.
 */

/** What a deployment bills in until the regime says otherwise. */
export const DEFAULT_CURRENCY = 'INR'
export const DEFAULT_LOCALE = 'en-IN'

let defaults = { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE }

/**
 * Point the formatters at the jurisdiction in force. Called once, when the regime loads.
 *
 * A module-level default rather than a prop threaded through forty components: every
 * figure on every screen belongs to the same deployment, and the exceptions -- documents
 * raised under a regime that is no longer in force -- say so at the call site instead.
 */
export function setMoneyDefaults(currency?: string | null, locale?: string | null): void {
  defaults = {
    currency: (currency || DEFAULT_CURRENCY).toUpperCase(),
    locale: locale || DEFAULT_LOCALE,
  }
}

const CACHE = new Map<string, Intl.NumberFormat>()

function formatter(
  currency: string | null | undefined,
  symbol: boolean,
  locale?: string | null,
): Intl.NumberFormat {
  const code = (currency || defaults.currency).toUpperCase()
  const tag = locale || defaults.locale
  const key = `${tag}:${code}:${symbol}`
  let found = CACHE.get(key)
  if (!found) {
    found = new Intl.NumberFormat(tag, {
      ...(symbol ? { style: 'currency' as const, currency: code } : {}),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    CACHE.set(key, found)
  }
  return found
}

/**
 * A decimal string as an amount of money: `"1250000.5"` -> `₹12,50,000.50`.
 *
 * `currency` and `locale` override the deployment default, for a document that carries its
 * own. Passing neither is the common case and means "however this deployment bills".
 *
 * `null` renders as an em dash rather than 0.00, because "no figure" and "nothing owed"
 * are different answers and a blank cell is the honest one.
 */
export function formatMoney(
  amount: string | number | null | undefined,
  currency?: string | null,
  locale?: string | null,
): string {
  if (amount == null || amount === '') return '—'
  const value = Number(amount)
  if (!Number.isFinite(value)) return '—'
  return formatter(currency, true, locale).format(value)
}

/**
 * The same figure with no currency symbol, for columns that carry it in the header.
 *
 * A ledger with two money columns repeats the symbol forty times down the page and the
 * digits stop lining up; the heading says rupees once instead.
 */
export function formatAmount(
  amount: string | number | null | undefined,
  locale?: string | null,
): string {
  if (amount == null || amount === '') return '—'
  const value = Number(amount)
  if (!Number.isFinite(value)) return '—'
  return formatter(null, false, locale).format(value)
}

/**
 * A signed figure that always shows its sign, for the round-off line on an invoice.
 *
 * A round-off of −0.40 printed as 0.40 makes a total that does not add up, which is the
 * one thing that line exists to explain.
 */
export function formatSigned(
  amount: string | number | null | undefined,
  currency?: string | null,
  locale?: string | null,
): string {
  if (amount == null || amount === '') return '—'
  const value = Number(amount)
  if (!Number.isFinite(value)) return '—'
  const sign = value > 0 ? '+' : value < 0 ? '−' : ''
  return `${sign}${formatMoney(Math.abs(value), currency, locale)}`
}

/**
 * `"18.000"` -> `18%`, `"8.375"` -> `8.375%`. Trailing zeroes on a tax rate are noise.
 *
 * Three decimals kept, not two: a US combined rate of 8.375% is real, and 8.38% on the
 * screen beside a line priced at 8.375% is a figure the customer cannot reconcile.
 */
export function formatPercent(
  rate: string | number | null | undefined,
  locale?: string | null,
): string {
  if (rate == null || rate === '') return '—'
  const value = Number(rate)
  if (!Number.isFinite(value)) return '—'
  const tag = locale || defaults.locale
  return `${new Intl.NumberFormat(tag, { maximumFractionDigits: 3 }).format(value)}%`
}

/**
 * A weighted-average unit cost, at the four decimals the column carries.
 *
 * Not `formatMoney`: a case of 24 bought at a rupee a unit averages to a third of a paisa,
 * and rounding that to two decimals on screen makes `quantity x avg_cost` disagree with
 * the value printed beside it -- which reads as a bug in the valuation rather than as
 * rounding. Two decimals minimum, so a cost of 50 still lines up under one of 50.3333.
 */
export function formatCost(
  amount: string | number | null | undefined,
  locale?: string | null,
): string {
  if (amount == null || amount === '') return '—'
  const value = Number(amount)
  if (!Number.isFinite(value)) return '—'
  const tag = locale || defaults.locale
  return new Intl.NumberFormat(tag, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value)
}
